import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { currencyService } from '../services/currencyService';
import { categoryService, Category } from '../services/categoryService';
import { getAllServices, type Service } from '../services/servicesStore';
import {
  getServiceBillingLabel,
  getServiceOptionsSummary,
  getServicePricePrefix,
  getServicePrimaryCtaLabel,
  hasStartingPrice,
} from '../services/serviceCatalogUtils';
import { absoluteUrl } from '../lib/seo';

const ITEMS_PER_PAGE = 12;

const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-pulse">
    <div className="aspect-video bg-gray-200" />
    <div className="p-6 space-y-4">
      <div className="h-6 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-1/2" />
      <div className="h-8 bg-gray-200 rounded w-full" />
    </div>
  </div>
);

const Services = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [, setDisplayCountry] = useState(currencyService.getCountry());

  useEffect(() => {
    Promise.all([getAllServices(), categoryService.getCategories()])
      .then(([servicesData, categoriesData]) => {
        setServices(servicesData || []);
        setCategories(categoriesData || []);
      })
      .catch((error) => {
        console.error('Erreur de chargement du catalogue:', error);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
    setCurrentPage(1);
  };

  const filteredServices = services.filter((service) => {
    const matchesCategory =
      selectedCategories.length === 0 || selectedCategories.includes(service.category);
    const matchesSearch =
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
  const paginatedServices = filteredServices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatPrice = (price: number) => currencyService.formatPrice(price);
  const servicesSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: paginatedServices.map((service, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: absoluteUrl(`/acheter/${service.id}`),
      name: service.name,
    })),
  };

  return (
    <main>
      <Helmet>
        <title>Services Digitaux | Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Agence digitale orientee resultats: creation de site web, acquisition media, CRM, IA, tracking, automatisation et croissance commerciale."
        />
        <link rel="canonical" href={absoluteUrl('/services')} />
        <meta property="og:title" content="Services Digitaux | Couteau Suisse Digital" />
        <meta
          property="og:description"
          content="Services digitaux orientes performance pour accelerer vos ventes, votre acquisition et votre execution marketing."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={absoluteUrl('/services')} />
        <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      </Helmet>

      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-neon-orange">
              Services CSD
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              Des solutions digitales pensees pour la croissance de votre entreprise
            </h1>
            <p className="mt-3 max-w-4xl text-gray-600">
              Du site web a l&apos;acquisition, en passant par le CRM, l&apos;IA, le tracking et
              l&apos;automatisation, chaque offre est structuree pour un objectif commercial
              clair. Vous activez ensuite uniquement les options utiles a votre contexte.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-gray-500">
              <span>Offres activables en ligne</span>
              <span>Parcours d&apos;achat clair</span>
              <span>{filteredServices.length} solution{filteredServices.length > 1 ? 's' : ''} affichee{filteredServices.length > 1 ? 's' : ''}</span>
            </div>
          </div>

          <input
            type="text"
            placeholder="Rechercher un service, une categorie ou un besoin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full mb-6 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-neon-orange"
          />

          <div className="flex flex-wrap gap-3 mb-8">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => toggleCategory(cat.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategories.includes(cat.name)
                    ? 'bg-neon-orange text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Liste des services */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading
              ? Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => <SkeletonCard key={i} />)
              : paginatedServices.map((service) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-neon-orange/10 to-transparent rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative bg-white rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 border border-gray-100">
                      {service.image && (
                        <div className="aspect-video w-full overflow-hidden">
                          <img
                            src={service.image}
                            alt={service.name}
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        {service.popular && (
                          <div className="absolute top-4 right-4 bg-neon-orange text-white text-sm px-4 py-1 rounded-full">
                            Populaire
                          </div>
                        )}
                        <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                        <p className="text-gray-600 mb-4">{service.description}</p>
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                              {getServicePricePrefix(service)}
                            </div>
                            <div className="text-2xl font-bold text-neon-orange">
                              {formatPrice(service.price)}
                            </div>
                            <div className="mt-1 text-sm text-gray-500">
                              {getServiceBillingLabel(service)}
                            </div>
                          </div>
                          <div className="text-sm text-gray-500 text-right">
                            {currencyService.canUseKlarna(
                              service.price,
                              service.billingType ?? 'one-time'
                            ) && !hasStartingPrice(service)
                              ? `3x Klarna ${formatPrice(
                                  currencyService.getKlarnaInstallmentAmount(service.price)
                                )}`
                              : getServiceOptionsSummary(service) || 'Base agence'}
                          </div>
                        </div>
                        <Link
                          to={`/acheter/${service.id}`}
                          className="block w-full bg-gradient-to-r from-neon-orange to-[#FF4500] text-white py-3 rounded-full font-semibold text-center relative overflow-hidden group"
                        >
                          <span className="relative z-10">
                            {getServicePrimaryCtaLabel(service)}
                          </span>
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                            initial={{ x: '-100%' }}
                            whileHover={{ x: '100%' }}
                            transition={{ duration: 1 }}
                          />
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
          </div>

          {totalPages > 1 && !isLoading && (
            <div className="flex justify-center gap-2 mt-10">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-4 py-2 rounded ${
                    currentPage === i + 1
                      ? 'bg-neon-orange text-white'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Services;
